import { useEffect } from 'react'

import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form'

/**
 * Syncs an external value (e.g. from a query) into a react-hook-form field
 * and re-validates a dependent field when it changes.
 *
 * This is needed because RHF's resolver only re-runs validation when form
 * fields change, not when external data changes. `setValue` with
 * `shouldValidate` only validates the field being set, not fields that
 * depend on it via `superRefine`.
 *
 * Skips the revalidate trigger when the dependent field is unpopulated —
 * otherwise we'd surface baseline "required" errors before the user has
 * interacted with the field.
 */
export function useSyncExternalValue<T extends FieldValues>({
  methods,
  field,
  value,
  revalidates,
}: {
  methods: UseFormReturn<T>
  field: Path<T>
  value: PathValue<T, Path<T>>
  revalidates: Path<T>
}) {
  useEffect(() => {
    if (methods.getValues(field) === value) return
    methods.setValue(field, value)
    const dependentValue = methods.getValues(revalidates)
    if (dependentValue === undefined || dependentValue === '') return
    void methods.trigger(revalidates)
  }, [methods, field, value, revalidates])
}
